import { getSawKnife } from '@/util/LayoutFuncs'
import ClipperLib from '@/util/clipper_unminified.js'
import { dealHighlightMatCode, dealNewSurplusTrimEdge } from '@/util/commonFun'
import { uniquePlankNum } from '@/util/commonFuncs'
import { getPlateKnifeDiameter } from '@/util/dealPaibanData'
import { expandPath } from '@/util/dealPlankDataFuncs'
import {
  dealPathByNCSetting,
  dealSurplusSize,
  getEdgeLengthsFromLShape,
  getWHByPath,
  simplifyPolygon,
  toUpperPointPath,
} from '@/util/plankCommonFuncs'
import { getByToken, postByToken } from '@/util/vueRequest'

import { rotatePart } from './LayoutTool'

export class SurplusPayloadBuildError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'SurplusPayloadBuildError'
  }
}

/**
 * 与 drawAllParts.genStockKey 一致：用于余料匹配/入库 stockKey
 */
export function genSurplusStockKey(plank: any): string {
  const { matCode, texture, thick, surplusPath } = plank
  const { min_width, min_long, width, long } = dealSurplusSize(plank)
  const stockKey = `${matCode}-${texture}-${thick}-${long}-${width}`
  return surplusPath[0].length <= 4
    ? stockKey
    : stockKey + `-${min_long}-${min_width}`
}

/**
 * 单块裁剪余料 -> create_surplus_depot 单条 surplus_data（不含 name，由接口下次序生成）
 */
export function buildCutSurplusDepotPayloadItem(
  part: any,
  ncSetting: any
): Record<string, any> {
  const plank = window.$copy(part)
  const texDir = plank.texDir
  if (texDir == 'reverse') {
    rotatePart(plank)
  }

  const {
    matCode,
    texture,
    thick,
    surplusPath,
    from_address,
    from_order_num,
    path,
    plank_remarks,
    blueAreaSurplus,
    _oriMatCode,
    _oriTexture,
    oriTexture,
    highlight_surface,
    is_high_gloss_plank,
  } = plank
  const specialShapeObj: { path: any[] } = { path: [] }
  const polygon = surplusPath ?? path

  if (polygon) {
    const sizeRect = getWHByPath(polygon[0])
    if (sizeRect.width <= 10 || sizeRect.height <= 10) {
      throw new SurplusPayloadBuildError(
        '入库失败，存在余料尺寸过小，请前往对应生产线设置余料最小尺寸！'
      )
    }
    const simplifiedPath = simplifyPolygon(polygon[0])
    if (simplifiedPath.length > 3) {
      const upperPath = toUpperPointPath(simplifiedPath)
      const stockKeyForKnife = `${plank.texture}:${plank.matCode}:${plank.thick}`
      const diameter = plank.isUseSaw
        ? getSawKnife(ncSetting)
        : +getPlateKnifeDiameter(stockKeyForKnife, ncSetting)
      const realPath = expandPath(-diameter / 2, upperPath)
      specialShapeObj.path = realPath[0]
    }
  }
  const size = (
    specialShapeObj.path.length && [5, 6].includes(specialShapeObj.path.length)
      ? getEdgeLengthsFromLShape(specialShapeObj.path)
      : dealSurplusSize(plank)
  ) as any
  specialShapeObj.path = dealPathByNCSetting(specialShapeObj.path, ncSetting)
  const area = Math.abs(ClipperLib.Clipper.Area(specialShapeObj.path))
  const shapeMap: Record<number, number> = {
    4: 0,
    5: 0,
    6: 1,
    7: 1,
    8: 2,
    9: 2,
  }
  const shape = shapeMap[specialShapeObj.path.length]
  const long = size.longestVerticalEdge?.size ?? size.long
  const width = size.longestHorizontalEdge?.size ?? size.width
  const min_long = size.secondLongestVerticalEdge?.size ?? size.min_long
  const min_width = size.secondLongestHorizontalEdge?.size ?? size.min_width

  return {
    stockKey: genSurplusStockKey(plank),
    type: _oriMatCode || matCode,
    color: _oriTexture || oriTexture || texture,
    thick,
    width,
    long,
    from_address,
    from_order_num,
    area: (shape ? Number(area) : width * long) / 1000 / 1000,
    texDir,
    shape,
    min_long,
    min_width,
    amount: 1,
    plankNum: uniquePlankNum.createUniquePlankNum(plank, true),
    remark: plank_remarks ?? '',
    brandName: plank.brandName || '',
    supplierName: plank.supplierName || '',
    color_id: plank.colorId || '',
    blueAreaSurplus,
    highlight_surface,
    is_high_gloss_plank,
    ...specialShapeObj,
  }
}

export function buildCutSurplusDepotPayloadItems(
  planks: any[],
  ncSetting: any
): Record<string, any>[] {
  return planks.map((p) => buildCutSurplusDepotPayloadItem(p, ncSetting))
}

/** 与排版页一致：名称余料{n} 递增 */
export function assignSequentialSurplusNames(
  items: any[],
  nextSurplusName: string
): void {
  const surplusIndex = nextSurplusName.split('余料')[1]
  const base = Number(surplusIndex)
  items.forEach((e, index) => {
    e.name = '余料' + (base + index)
    e.amount = 1
  })
}

/** XY 互换、修边、去掉蓝色余料临时字段（与 drawAllParts 入库前处理一致） */
export function postProcessSurplusPayloadItems(
  items: any[],
  ncSetting: any
): void {
  items.forEach((e) => {
    if (ncSetting.xyReverse) {
      const temp = e.long
      e.long = e.width
      e.width = temp
      if (e.min_long && e.min_width) {
        const tempMin = e.min_long
        e.min_long = e.min_width
        e.min_width = tempMin
      }
    }
    dealNewSurplusTrimEdge(e)
    delete e.blueAreaSurplus
  })
}

/** 上传前异形余料 min 置 0 */
export function applySpecialShapeMinZeroForUpload(items: any[]): void {
  items.forEach((item) => {
    if (item.shape == 2) {
      item.min_long = 0
      item.min_width = 0
    }
  })
}

/**
 * @description 获取下一个余料默认名称序号（get_user_plank_setting）
 */
export function fetchNextSurplusNameSetting(): Promise<{
  nextSurplusName: string
} | null> {
  return new Promise((resolve) => {
    getByToken('/get_user_plank_setting', {}, (res: any) => {
      if (res?.status === 1) {
        resolve({ nextSurplusName: res.data.next_surplus_name })
      } else {
        resolve(null)
      }
    })
  })
}

/**
 * @description 创建余料入库（与 $token create_surplus_depot 一致）
 */
export function requestCreateSurplusDepot(
  surplusData: any[],
  designated: boolean
): Promise<any> {
  return new Promise((resolve) => {
    postByToken(
      '/create_surplus_depot',
      { surplus_data: surplusData, designated: designated ? true : false },
      (res: any) => resolve(res)
    )
  })
}

type RestockingQrOptions = {
  uid: number
  page?: number
}

/**
 * 裁剪余料标签「余料入库码」二维码数据：与入库接口字段、XY 互换后尺寸一致
 * 严格对齐 createTag.js(2868-2888) 的字段：uid/m/t/s/p/r/path（可选）
 */
export function buildCutSurplusRestockingQrPlankInfo(
  cutPart: any,
  ncSetting: any,
  { uid, page = 1 }: RestockingQrOptions
): Record<string, unknown> {
  const raw = buildCutSurplusDepotPayloadItem(cutPart, ncSetting)
  const item = JSON.parse(JSON.stringify(raw))
  postProcessSurplusPayloadItems([item], ncSetting)
  applySpecialShapeMinZeroForUpload([item])
  const path =
    Array.isArray(item.path) && item.path.length > 0 ? item.path : undefined
  const s = `${page}-${item.long}-${item.min_long ?? 0}-${item.width}-${
    item.min_width ?? 0
  }-${item.thick}-${item.shape ?? 0}`
  return {
    uid,
    m: dealHighlightMatCode(item.type),
    d: item.texDir,
    h: item.highlight_surface,
    t: item.color,
    s,
    p: item.plankNum,
    r: item.remark ?? '',
    ...(path ? { path } : {}),
  }
}
